import { useSyncExternalStore } from "react";
import { base_url } from "constant/constant";
import en from "./en.json";
import fr from "./fr.json";

export type Locale = "en" | "fr";

export const LOCALES: Locale[] = ["en", "fr"];

/**
 * The visitor's own choice, made with the language switcher. Kept apart from
 * the detected default below because the two must not be confused: a visitor
 * in Paris who switches to English has to stay in English, and that is only
 * knowable if an explicit choice looks different from a detected one.
 */
const STORAGE_KEY = "europea.site.locale";

/** The last answer from the country lookup, and when it was given. */
const GEO_KEY = "europea.site.locale.geo";

/**
 * How long a detected language is reused before asking again.
 *
 * Long enough that the lookup happens once a day at most rather than on every
 * visit, short enough that someone who has actually moved country is not held
 * in the wrong language for a week.
 */
const GEO_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The lookup is on the path to the first paint, so it is given a short leash.
 * Past this the site opens in English and corrects itself if the answer
 * arrives later.
 */
const GEO_TIMEOUT_MS = 1500;

const DICTIONARIES: Record<Locale, Record<string, unknown>> = { en, fr };

const isLocale = (value: unknown): value is Locale =>
  value === "en" || value === "fr";

/** The visitor's explicit choice, if they have ever made one. */
const readChosen = (): Locale | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    // Private mode / storage disabled: fall back rather than break the page.
    return null;
  }
};

type GeoCache = { locale: Locale; at: number };

const readGeo = (): GeoCache | null => {
  try {
    const raw = window.localStorage.getItem(GEO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeoCache>;
    if (!isLocale(parsed?.locale) || typeof parsed?.at !== "number") return null;
    return { locale: parsed.locale, at: parsed.at };
  } catch {
    return null;
  }
};

const writeGeo = (locale: Locale) => {
  try {
    window.localStorage.setItem(GEO_KEY, JSON.stringify({ locale, at: Date.now() }));
  } catch {
    // Non-fatal: the lookup just runs again on the next visit.
  }
};

/**
 * Module-level store (not a context provider) so any component — including
 * ones rendered outside the router tree — can read/set the locale with no
 * wrapper. Separate storage key and store from TenantPlatform/i18n/locale.ts:
 * that one is deliberately scoped to the onboarding flow only — a tenant's
 * contract language is chosen by the back office and must not change because
 * they opened the link from another country.
 */
const read = (): Locale => readChosen() ?? readGeo()?.locale ?? "en";

let current: Locale = read();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => current;

/** Keeps <html lang> honest for screen readers and search engines. */
const applyDocumentLang = (locale: Locale) => {
  try {
    document.documentElement.lang = locale;
  } catch {
    // No document (tests, SSR-style tooling): nothing to keep in sync.
  }
};

const emit = (next: Locale) => {
  if (next === current) return;
  current = next;
  applyDocumentLang(next);
  listeners.forEach((listener) => listener());
};

export const setLocale = (next: Locale) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
  emit(next);
};

/**
 * A country asked for in the page URL, as `?country=FR`.
 *
 * The detection cannot otherwise be exercised anywhere it matters: a developer
 * machine reaches the API over loopback, which has no country, so the site
 * always opens in English there however the rule is written. This forwards the
 * request to the endpoint's own override and drives the real path — real
 * request, real rule — from the address bar.
 *
 * Nothing is trusted here: the answer still comes from the API, and the worst
 * a visitor can do with it is change the language of their own page, which the
 * switcher does anyway.
 */
const overrideCountry = (): string | null => {
  try {
    const value = new URLSearchParams(window.location.search).get("country");
    const code = value?.trim().toUpperCase() || "";
    return /^[A-Z]{2}$/.test(code) ? code : null;
  } catch {
    return null;
  }
};

/**
 * Ask the API which language this visitor's country opens in.
 *
 * The country rule itself lives on the server — the site asks what to show,
 * not where the visitor is — so all that arrives here is "en" or "fr". Any
 * failure resolves to null and leaves the current language alone; a page that
 * cannot reach the lookup still has to open.
 */
const fetchGeoLocale = async (): Promise<Locale | null> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
    const country = overrideCountry();
    const url = country
      ? `${base_url}/geo/locale?country=${encodeURIComponent(country)}`
      : `${base_url}/geo/locale`;
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!response.ok) return null;
    const data = await response.json();
    return isLocale(data?.locale) ? data.locale : null;
  } catch {
    return null;
  }
};

/**
 * Settle the language before the site is drawn.
 *
 * Three cases, and only one of them waits on the network:
 *
 *  - The visitor has chosen a language. It wins, for ever; nothing is asked.
 *  - A recent detection is cached. Applied at once, no request.
 *  - Nothing is known. The lookup is awaited — this is the first visit, and
 *    opening in English only to swap to French a moment later is worse than
 *    the wait. A stale cache is applied first and refreshed in the background,
 *    so only a genuinely first visit ever waits.
 */
export const initLocale = async (): Promise<void> => {
  applyDocumentLang(current);

  // ?country= outranks everything, including a stored choice: it exists to be
  // watched, and a language remembered from an earlier visit would swallow the
  // very thing being tested. Nothing is written for it either, so dropping the
  // parameter puts the site straight back to normal.
  const forced = overrideCountry();
  if (forced) {
    const locale = await fetchGeoLocale();
    if (locale) emit(locale);
    return;
  }

  if (readChosen()) return;

  const cached = readGeo();
  const fresh = cached && Date.now() - cached.at < GEO_TTL_MS;

  if (cached) emit(cached.locale);
  if (fresh) return;

  const resolve = fetchGeoLocale().then((locale) => {
    if (!locale) return;
    // The visitor may have used the switcher while this was in flight; their
    // choice outranks the lookup.
    if (readChosen()) return;
    writeGeo(locale);
    emit(locale);
  });

  // With something already on screen there is nothing to wait for: let the
  // refresh land whenever it lands.
  if (cached) return;
  await resolve;
};

const getByPath = (dict: Record<string, unknown>, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined,
      dict
    );

export type TranslateParams = Record<string, string | number>;

/**
 * Falls back to the English string (then the raw key) rather than rendering
 * blank, so a translation that hasn't been added to fr.json yet is still
 * readable instead of an empty label.
 *
 * `params` fills `{{name}}` placeholders. Keeping the whole sentence in the
 * dictionary — rather than concatenating translated fragments in the caller —
 * is what lets a language reorder it: "12 properties in Paris" and
 * "12 propriétés à Paris" happen to share a word order, but the next language
 * need not. An unmatched placeholder is left as-is so a missing param shows up
 * as `{{name}}` instead of silently rendering "undefined".
 */
export const translate = (
  locale: Locale,
  key: string,
  params?: TranslateParams
): string => {
  const value = getByPath(DICTIONARIES[locale], key);
  const fallback = getByPath(DICTIONARIES.en, key);
  const template =
    typeof value === "string"
      ? value
      : typeof fallback === "string"
      ? fallback
      : key;

  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) =>
    params[name] !== undefined ? String(params[name]) : match
  );
};

export const useLocale = () => {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const t = (key: string, params?: TranslateParams) =>
    translate(locale, key, params);
  return { locale, setLocale, t };
};
